"use client";

import { c as _c } from "react/compiler-runtime";
import { queryClient } from "@/config/react-query";
import { useTenant } from "@/contexts/tenant-provider";
import { ModifierDescriptionMap, buildModifierDescriptionMap, normalizeModifierCodes } from "@/lib/billing/modifier-descriptions";
import { formatDoseComponentAmount, getDoseComponents, getDosePrimaryLabel } from "@/lib/dose-components";
import { formatDate, formatHeight, formatName, formatPhoneNumber, formatStaffName, formatTime, formatWeight } from "@/lib/format-data";
import { escapeNoteHtml } from "@/lib/note-utils";
import { nameShowsCredentials } from "@/lib/staff-name-display";
import { useToast } from "@/lib/use-toast";
import { RoleType } from "@/types/enums";
import { MeasurementSummary } from "@/types/measurement";
import { NoteListItem } from "@/types/notes";
import type { RoleSummary } from "@/types/provider";
import { ProviderTiny } from "@/types/provider";
import { ServiceDetail } from "@/types/service";
import { PrivateTenant } from "@/types/tenant";
import { medicalCodeQueryOptions } from "@/api/medical-codes-query";
import { providerQueryOptions } from "@/api/provider/admin/provider-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
import { calendarQueryOptions } from "@/query/check-in/calendar/calendar-query";

// ============================================================================
// Shared print-via-window helpers
// ============================================================================

/**
 * Kept as an export here because callers already import it from this module;
 * the implementation lives in note-utils, which is the lighter of the two and
 * needs it to build the printed note body.
 */
export const escapeHtml = escapeNoteHtml;

/**
 * The distinct, normalized modifier codes a note carries, across every CPT code
 * it has modifiers for. Exported so the wiring that feeds the printed note's
 * labels is testable without driving the whole print flow.
 */
export function noteModifierCodes(noteItem: NoteListItem): string[] {
  return normalizeModifierCodes(Object.values(noteItem.cptModifiers ?? {}).flat());
}
function renderNoteMedicalCodes(noteItem: NoteListItem, modifierDescriptions?: ModifierDescriptionMap): string {
  const icd10Codes = (noteItem.icd10Codes ?? []).filter(code => code.code.trim());
  const cptCodes = (noteItem.cptCodes ?? []).filter(code => code.code.trim());
  if (icd10Codes.length === 0 && cptCodes.length === 0) return "";
  const renderCode = (code: string, description: string | undefined, modifiers: string[] = []) => {
    const normalizedModifiers = modifiers.map(modifier => modifier.trim()).filter(Boolean);
    // The CPT and ICD-10 codes above already print with their descriptions, so
    // a bare modifier chip was the only code on the document without one. Each
    // modifier stacks onto its own row so a ~180-character AMA label can wrap
    // instead of stretching the chip row; a modifier with no known description
    // still prints as the bare chip it always was.
    return `<div class="medical-code-item">
      <div class="medical-code-main">
        <span class="medical-code-value">${escapeHtml(code)}</span>
        ${description ? `<span class="medical-code-description">${escapeHtml(description)}</span>` : ""}
      </div>
      ${normalizedModifiers.length > 0 ? `<div class="medical-code-modifiers">
              <span class="medical-code-modifiers-label">Modifiers</span>
              <div class="medical-code-modifier-list">
                ${normalizedModifiers.map(modifier => {
      const label = modifierDescriptions?.[modifier.toUpperCase()];
      return `<div class="medical-code-modifier-row">
                        <span class="medical-code-modifier">${escapeHtml(modifier)}</span>
                        ${label ? `<span class="medical-code-modifier-description">${escapeHtml(label)}</span>` : ""}
                      </div>`;
    }).join("")}
              </div>
            </div>` : ""}
    </div>`;
  };
  return `
    <div class="medical-codes-info">
      <h2 class="medical-codes-title">Medical Codes</h2>
      <div class="medical-codes-grid">
        ${icd10Codes.length > 0 ? `<div class="info-item">
                <div class="info-label">ICD-10 Diagnosis Codes</div>
                <div class="medical-code-list">
                  ${icd10Codes.map(code => renderCode(code.code, code.shortDesc || code.longDesc)).join("")}
                </div>
              </div>` : ""}
        ${cptCodes.length > 0 ? `<div class="info-item">
                <div class="info-label">CPT / HCPCS Procedure Codes</div>
                <div class="medical-code-list">
                  ${cptCodes.map(code => renderCode(code.code, code.description, noteItem.cptModifiers?.[code.code])).join("")}
                </div>
              </div>` : ""}
      </div>
    </div>`;
}

/**
 * Build a single-line address string (street, line two, "City, ST ZIP") for
 * the printed letterhead. Returns "" when no components are present so callers
 * can omit the line entirely. The two source models name the street field
 * differently (provider/user `address`, location `addressLineOne`), so the
 * caller passes whichever it has as `street`.
 */
export function formatAddressLine(parts: {
  street?: string | null;
  addressLineTwo?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}): string {
  const cityStateZip = [parts.city?.trim(), [parts.state?.trim(), parts.zipCode?.trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [parts.street?.trim(), parts.addressLineTwo?.trim(), cityStateZip].filter(Boolean).join(", ");
}

/**
 * Write HTML into an already-open window and trigger print.
 * Use when the window must be opened before async work (to avoid popup blockers).
 */
export function writeToPrintWindow(printWindow: Window, html: string): void {
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}

/**
 * Opens a new browser window, writes the given HTML, and triggers print.
 * Returns an error if the popup was blocked.
 */
export function openPrintWindow(html: string): {
  success: true;
} | {
  success: false;
  error: string;
} {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    return {
      success: false,
      error: "Unable to open print window. Please check your browser settings."
    };
  }
  writeToPrintWindow(printWindow, html);
  return {
    success: true
  };
}
interface PrintMarkdownOptions {
  content: string;
  noteItem: NoteListItem;
  tenant: PrivateTenant | null;
  /**
   * When true, the Prescribed Medications block is omitted entirely — not even
   * the medication names print. Takes precedence over `hideMedicationDosage`,
   * which only redacts the dosing detail. Defaults to false (block shown).
   */
  hideMedications?: boolean;
  /**
   * When true, prescribed medications print with the medication name only —
   * dose, frequency, quantity, administration, lot number, and side effects are
   * suppressed. Defaults to false (full dosage shown).
   */
  hideMedicationDosage?: boolean;
  /**
   * When true, the Medical Information block is omitted — patient-reported
   * medications and the patient's health-intake fields. Defaults to false
   * (section shown).
   */
  hideMedicalInformation?: boolean;
  /**
   * Raw HTML for the note's photo section (see `buildNotePhotosHtml`). Printed
   * after the clinical content and before the sign-off signatures. Empty when
   * the note has no photos or the provider left them out.
   *
   * Pass a thunk when producing the HTML is slow: it is invoked *after* the
   * print window has been opened. `window.open` only succeeds while the click's
   * transient user activation is live (~5s in Chrome), so awaiting a dozen photo
   * downloads before calling it gets the popup blocked.
   */
  photosHtml?: string | (() => Promise<string>);
  /**
   * Sign-off signature markdown, kept out of `content` so the photos can sit
   * between the clinical content and the signature.
   */
  signatureContent?: string;
  /**
   * The kind of encounter this note documents (e.g. "Preoperative History and
   * Physical"). Currently the note template's name, which is the closest signal
   * already on the note. Omitted from the header when unknown.
   */
  encounterType?: string;
  /**
   * Reference descriptions for the note's billing modifiers, keyed by
   * upper-cased code. Each modifier prints its label beside the code chip;
   * codes missing from the map print as the bare chip.
   *
   * Pass a thunk when the map still has to be fetched: like `photosHtml`, it is
   * invoked *after* the print window has been opened. `window.open` only
   * succeeds while the click's transient user activation is live, and awaiting a
   * lookup first would trade the whole printout for a few labels.
   */
  modifierDescriptions?: ModifierDescriptionMap | (() => Promise<ModifierDescriptionMap>);
  /**
   * Data URL for the clinic logo in the document header, replacing the tenant's
   * remote logo URL. The print window loads that URL itself, but the fax path
   * rasterizes the document in-page, where a cross-origin logo cannot be read
   * back out of the canvas — so that caller inlines the bytes first.
   */
  logoDataUrl?: string | null;
}
export type BuildNotePrintHtmlResult = {
  success: true;
  html: string;
} | {
  success: false;
  error: string;
};

/**
 * Assemble the printable note document as a standalone HTML string.
 *
 * Split out of `printMarkdown` so the same document can be routed somewhere
 * other than a print window — the fax flow renders this HTML to PDF bytes
 * (`renderPrintHtmlToPdf`). Keeping one builder means a note that is faxed and
 * a note that is printed are the same document, honoring the same dialog
 * exclusions, rather than two layouts that drift apart.
 */
export const buildNotePrintHtml = async ({
  content,
  noteItem,
  tenant,
  hideMedications = false,
  hideMedicationDosage = false,
  hideMedicalInformation = false,
  photosHtml = "",
  signatureContent = "",
  encounterType = "",
  modifierDescriptions,
  logoDataUrl
}: PrintMarkdownOptions): Promise<BuildNotePrintHtmlResult> => {
  try {
    // Thunks are resolved here rather than by the caller: `printMarkdown` opens
    // its window before calling in, so the slow work still lands after
    // `window.open` — see `photosHtml` on PrintMarkdownOptions.
    const resolvedPhotosHtml = typeof photosHtml === "function" ? await photosHtml() : photosHtml;

    // Fetch complete patient data with all demographics and health data
    const fullPatient = await queryClient.fetchQuery(patientQueryOptions.detail(noteItem.patient.id));
    if (!fullPatient) {
      return {
        success: false,
        error: "Unable to fetch complete patient information for this note."
      };
    }
    const patientName = formatName(noteItem.patient.firstName, noteItem.patient.lastName);
    // Clinical records index patients by surname first. Each part goes through
    // formatName so the casing normalization applies here too.
    const patientNameLastFirst = noteItem.patient.lastName ? `${formatName(noteItem.patient.lastName, undefined)}, ${formatName(noteItem.patient.firstName, undefined)}` : patientName;
    const visitDateValue = noteItem.visitDate ?? noteItem.createdDate;
    // Date *and* time: two encounters on the same calendar day are otherwise
    // indistinguishable on the exported note.
    const visitDate = `${formatDate(visitDateValue, true, true)} ${formatTime(visitDateValue)}`.trim();
    // When the entry was documented, which is a property of the note — not of
    // this printout. Reading it off `new Date()` restamped the note on every
    // reprint and made a late entry look contemporaneous.
    const documentedValue = noteItem.lastEditedDate ?? noteItem.createdDate;
    const documentedDate = formatDate(documentedValue, true, true);
    const documentedTime = formatTime(documentedValue);
    // `noteItem.providers` is the distinct set of authors across the note's
    // revision chain, so it names editors as readily as clinicians. The signer
    // is the one provider of record we actually hold, so a signed note
    // attributes the service to them; anything else prints the author list
    // under the weaker "Provider" label rather than asserting who rendered it.
    const signingProvider = noteItem.signedOffProviderId ? noteItem.providers.find(p => p.id === noteItem.signedOffProviderId) : undefined;
    const headerProviders = signingProvider ? [signingProvider] : noteItem.providers;
    const providerLabel = signingProvider ? "Rendering Provider" : headerProviders.length > 1 ? "Providers" : "Provider";
    const revisionNumber = noteItem.version;

    // Letterhead merge fields: the signing provider's address and the
    // clinic (location) address/phone. Best-effort — a failed fetch just omits
    // the letterhead rather than blocking the printout.
    //
    // Prefer the note's signer so patient exports show the signing provider's
    // letterhead rather than an earlier editor's; fall back to the first
    // provider when there is no signer.
    const letterheadProviderId = noteItem.signedOffProviderId ?? noteItem.providers[0]?.id;
    let providerContactHtml = "";
    let clinicContactHtml = "";
    // Header fields resolved from the same lookups the letterhead needs.
    const providerCredentialsById = new Map<string, string>();
    let serviceLocationName = "";
    try {
      // Read contact fields from the unguarded provider list rather than the
      // admin getProvider endpoint (which requires users:read that note-signers
      // may lack), so the letterhead still renders for standard providers.
      // A note with no provider can still name its location of service, so the
      // location lookup is not gated on the letterhead provider.
      const [providers, locations] = letterheadProviderId || noteItem.locationId ? await Promise.all([queryClient.fetchQuery(providerQueryOptions.list()), queryClient.fetchQuery(calendarQueryOptions.locations(true))]) : [null, []];
      for (const provider of providers ?? []) {
        if (provider.credentials) {
          providerCredentialsById.set(provider.id, provider.credentials);
        }
      }
      serviceLocationName = (locations ?? []).find(l => l.id === noteItem.locationId)?.name ?? "";
      const providerDetail = providers?.find(p => p.id === letterheadProviderId) ?? null;
      if (providerDetail) {
        // Never print the provider's own phone number: it is frequently a
        // personal cell. The clinic's number is what a patient should call, so
        // fall back to the tenant's number when the location has none.
        const tenantPhone = tenant?.phoneNumber ? formatPhoneNumber(tenant.phoneNumber, false) : "";
        const clinic = (locations ?? []).find(l => l.id === providerDetail.primaryLocationId);
        const clinicPhone = clinic?.phoneNumber ? formatPhoneNumber(clinic.phoneNumber, false) : tenantPhone;

        // Only the provider's name goes in this block: their own address and
        // phone are personal contact details, and the clinic block below
        // carries the address and number a patient should use.
        const providerName = formatStaffName(providerDetail);
        // Fall back to the tenant phone here when there is no clinic block to
        // carry it — otherwise the same number would print twice.
        const providerPhone = clinic ? "" : tenantPhone;
        const providerLines = [providerPhone].filter(Boolean);
        if (providerName || providerLines.length > 0) {
          providerContactHtml = `
            <div class="letterhead-block">
              <div class="letterhead-name">${escapeHtml(providerName)}</div>
              ${providerLines.map(line => `<div class="letterhead-line">${escapeHtml(line)}</div>`).join("")}
            </div>`;
        }
        if (clinic) {
          const clinicAddress = formatAddressLine({
            street: clinic.addressLineOne,
            addressLineTwo: clinic.addressLineTwo,
            city: clinic.city,
            state: clinic.state,
            zipCode: clinic.zipCode
          });
          const clinicLines = [clinicAddress, clinicPhone].filter(Boolean);
          if (clinic.name || clinicLines.length > 0) {
            clinicContactHtml = `
            <div class="letterhead-block">
              ${clinic.name ? `<div class="letterhead-name">${escapeHtml(clinic.name)}</div>` : ""}
              ${clinicLines.map(line => `<div class="letterhead-line">${escapeHtml(line)}</div>`).join("")}
            </div>`;
          }
        }
      }
    } catch {
      // Letterhead is best-effort; keep printing even if the lookups fail.
    }
    const letterheadHtml = providerContactHtml || clinicContactHtml ? `<div class="letterhead">${clinicContactHtml}${providerContactHtml}</div>` : "";

    // The rendering provider is identified by name *and* credentials on a
    // clinical document. A provider with no credentials recorded still prints.
    const renderingProviderNames = headerProviders.length > 0 ? headerProviders.map((provider: ProviderTiny) => {
      const name = formatStaffName(provider);
      // Skip when the provider already chose to show credentials after their name.
      const credentials = nameShowsCredentials(provider) ? undefined : providerCredentialsById.get(provider.id);
      return credentials ? `${name}, ${credentials}` : name;
    }) : ["Unknown Provider"];

    // Additional patient demographics from complete patient data
    const dateOfBirth = fullPatient.dateOfBirth ? formatDate(fullPatient.dateOfBirth) : "Not provided";
    const gender = fullPatient.gender || "Not specified";
    const phoneNumber = fullPatient.phoneNumber ? formatPhoneNumber(fullPatient.phoneNumber, false) : "Not provided";
    const email = fullPatient.email ? fullPatient.email.toLowerCase() : "Not provided";
    const address = [fullPatient.address, fullPatient.addressLineTwo, fullPatient.city, fullPatient.state, fullPatient.zipCode].filter(Boolean).join(", ") || "Not provided";

    // Medical information from complete patient data
    const allMedications = fullPatient.medications || [];

    // Separate prescribed medications (with provider) from patient-reported ones (without provider)
    const prescribedMedications = allMedications.filter(med => med.providerId);

    // Dynamic health fields - get all available fields
    const healthData = fullPatient.healthData || {};

    // Get patient-reported medications from dynamic field
    const allPatientReportedMeds = healthData["Medications"]?.values || [];
    const combinedPatientMedications = allPatientReportedMeds.length > 0 ? Array.from(new Set(allPatientReportedMeds)).join(", ") : "";

    // Get ALL dynamic health fields - show everything
    const allHealthFields = Object.entries(healthData).map(([key, data]) => ({
      name: key,
      values: data.values?.join(", ") || "Not provided"
    }));

    // Get most recent value for each measurement type
    const measurements = fullPatient.measurements || [];
    const sortedMeasurements = measurements.sort((a, b) => new Date(b.measurementDate).getTime() - new Date(a.measurementDate).getTime());

    // Find most recent value for each measurement type
    const getLatestMeasurement = (field: keyof MeasurementSummary) => {
      return sortedMeasurements.find(m => m[field] != null);
    };
    const latestWeight = getLatestMeasurement("weight");
    const latestHeight = getLatestMeasurement("height");
    const latestBP = sortedMeasurements.find(m => m.systolicBp != null && m.diastolicBp != null);
    const latestPulse = getLatestMeasurement("pulse");
    const latestTemperature = getLatestMeasurement("temperature");

    // The measurements API returns weight in kg and height in cm while every
    // Decoda surface displays imperial — go through the shared formatters
    // instead of printing the raw API values. See
    // decoda/services/measurement/bridge.py.
    const weightDisplay = latestWeight ? formatWeight(latestWeight.weight) : null;
    const heightDisplay = latestHeight ? formatHeight(latestHeight.height) : null;

    // Guard the section on what will actually render, so a patient whose only
    // measurement is one this block does not print (e.g. waist) does not get an
    // empty "Recent Vital Signs & Measurements" heading.
    const hasVitalsToShow = Boolean(weightDisplay || heightDisplay || latestBP || latestPulse || latestTemperature);

    // The encounter type names the document on a clinical note ("History and
    // Physical"), so it titles the printout when known.
    const documentTitle = encounterType || "Clinical Note";
    // Resolved here, not before `window.open` above: a thunk means the caller
    // still has to fetch, and the popup must already exist by then.
    const resolvedModifierDescriptions = typeof modifierDescriptions === "function" ? await modifierDescriptions().catch(() => ({}) as ModifierDescriptionMap) : modifierDescriptions;
    const medicalCodesHtml = renderNoteMedicalCodes(noteItem, resolvedModifierDescriptions);

    // The identifier block a clinical note opens with: one flat label/value
    // list, clinical identifiers first, then patient contact details. Kept as a
    // plain list rather than titled card grids so it reads as a document header.
    // Every value here is user- or administrator-editable (a template name, a
    // provider's credentials, a location name) and the result goes to
    // `document.write` in a same-origin window, so the row escapes its value.
    // `idRowHtml` is the deliberate exception, for the one row that supplies
    // its own markup.
    const idRowHtml = (label: string, valueHtml: string) => valueHtml ? `<div class="id-row"><dt>${escapeHtml(label)}</dt><dd>${valueHtml}</dd></div>` : "";
    const idRow = (label: string, value: string) => idRowHtml(label, value ? escapeHtml(value) : "");

    // The chart number a clinic actually uses is the external ID carried over
    // from their previous system. Decoda's internal patient ID is not an MRN
    // and must not print as one, so it falls back under its own label.
    const mrnRow = fullPatient.externalId ? idRow("MRN", fullPatient.externalId) : idRow("Patient ID", noteItem.patient.id);
    const patientInfoHtml = `
      <h1 class="document-title">${escapeHtml(documentTitle)}</h1>
      <section class="encounter-header">
        <dl class="identifiers">
          ${idRow("Patient (Last, First)", patientNameLastFirst)}
          ${idRow("DOB", dateOfBirth)}
          ${mrnRow}
          ${idRow("Date of Service", visitDate)}
          ${idRow("Encounter Type", encounterType)}
          ${idRowHtml(providerLabel, renderingProviderNames.map((name: string) => escapeHtml(name.trim())).join("<br/>"))}
          ${idRow("Location of Service", serviceLocationName)}
          ${idRow("Documented", `${documentedDate} ${documentedTime}`)}
          ${revisionNumber > 1 ? idRow("Revision", String(revisionNumber)) : ""}
          ${idRow("Gender", gender)}
          ${idRow("Phone", phoneNumber)}
          ${idRow("Email", email)}
          ${idRow("Address", address)}
        </dl>
      </section>

      ${medicalCodesHtml}

      ${hasVitalsToShow ? `
      <div class="measurements-info">
        <h2 class="measurements-title">Recent Vital Signs & Measurements</h2>
        <div class="measurements-grid">
          ${latestWeight && weightDisplay ? `
          <div class="measurement-item">
            <div class="measurement-label">Weight</div>
            <div class="measurement-value">${weightDisplay}</div>
            <div class="measurement-date">${formatDate(new Date(latestWeight.measurementDate), true, true)}</div>
          </div>
          ` : ""}
          ${latestHeight && heightDisplay ? `
          <div class="measurement-item">
            <div class="measurement-label">Height</div>
            <div class="measurement-value">${heightDisplay}</div>
            <div class="measurement-date">${formatDate(new Date(latestHeight.measurementDate), true, true)}</div>
          </div>
          ` : ""}
          ${latestBP ? `
          <div class="measurement-item">
            <div class="measurement-label">Blood Pressure</div>
            <div class="measurement-value">${Math.round(latestBP.systolicBp!)}/${Math.round(latestBP.diastolicBp!)} mmHg</div>
            <div class="measurement-date">${formatDate(new Date(latestBP.measurementDate), true, true)}</div>
          </div>
          ` : ""}
          ${latestPulse ? `
          <div class="measurement-item">
            <div class="measurement-label">Pulse</div>
            <div class="measurement-value">${Math.round(latestPulse.pulse!)} bpm</div>
            <div class="measurement-date">${formatDate(new Date(latestPulse.measurementDate), true, true)}</div>
          </div>
          ` : ""}
          ${latestTemperature ? `
          <div class="measurement-item">
            <div class="measurement-label">Temperature</div>
            <div class="measurement-value">${Number(latestTemperature.temperature).toFixed(1)}°C</div>
            <div class="measurement-date">${formatDate(new Date(latestTemperature.measurementDate), true, true)}</div>
          </div>
          ` : ""}
        </div>
      </div>
      ` : ""}

      ${!hideMedications && prescribedMedications.length > 0 ? `
      <div class="medications-info">
        <h2 class="medications-title">Prescribed Medications</h2>
          ${prescribedMedications.map(med => {
      const components = getDoseComponents(med);
      if (hideMedicationDosage) {
        // Name-only mode keeps medication identity and provider attribution
        // while suppressing all dosing and administration details.
        return `
          <div class="medication-item">
            <div class="medication-header">
              <div class="medication-name">${getDosePrimaryLabel(med)}</div>
            </div>
            ${med.provider ? `<div class="medication-detail"><span class="detail-label">Provider:</span> ${formatStaffName(med.provider)}</div>` : ""}
          </div>
          `;
      }
      return `
          <div class="medication-item">
            <div class="medication-header">
              <div class="medication-name">${getDosePrimaryLabel(med)}</div>
              ${components.length ? `<div class="medication-dose">${components.map(formatDoseComponentAmount).join(" + ")}</div>` : ""}
            </div>
            ${med.provider ? `<div class="medication-detail"><span class="detail-label">Provider:</span> ${formatStaffName(med.provider)}</div>` : ""}
            ${med.frequency ? `<div class="medication-detail"><span class="detail-label">Frequency:</span> ${med.frequency}</div>` : ""}
            ${med.administrationLocation ? `<div class="medication-detail"><span class="detail-label">Administration:</span> ${med.administrationLocation}</div>` : ""}
            ${components.flatMap(component => component.lots ?? []).map(lot => lot.lotNumber).filter(Boolean).join(", ") ? `<div class="medication-detail"><span class="detail-label">Lot Number:</span> ${components.flatMap(component => component.lots ?? []).map(lot => lot.lotNumber).filter(Boolean).join(", ")}</div>` : ""}
            ${med.sideEffects ? `<div class="medication-detail"><span class="detail-label">Side Effects:</span> ${med.sideEffects}</div>` : ""}
            ${med.comment ? `<div class="medication-detail"><span class="detail-label">Notes:</span> ${med.comment}</div>` : ""}
            <div class="medication-date">Prescribed: ${formatDate(new Date(med.createdDate), true, true)}</div>
          </div>
          `;
    }).join("")}
      </div>
      ` : ""}

      ${hideMedicalInformation ? "" : `
      <div class="medical-info">
        <h2 class="medical-title">Medical Information</h2>
        <div class="medical-info-grid">
          ${combinedPatientMedications ? `
          <div class="info-item info-item-wide">
            <div class="info-label">Patient-Reported Medications</div>
            <div class="info-value">${combinedPatientMedications}</div>
          </div>
          ` : ""}
          ${allHealthFields.filter(field => field.name !== "Medications").map(field => `
          <div class="info-item info-item-wide">
            <div class="info-label">${field.name}</div>
            <div class="info-value">${field.values}</div>
          </div>
          `).join("")}
        </div>
      </div>
      `}
      <hr />
    `;
    const disclaimerHtml = `<div style="margin-top: auto; padding-top: 2rem;"><p style="font-size: 8pt; color: #6b7280; font-style: italic; margin: 0;">*This medical note was generated using Decoda Health's AI documentation system and has been reviewed by the attending provider.*</p></div>`;

    // The note body, photos and signature block all arrive as HTML already
    // sanitized by note-utils, so the document is concatenated directly. It used
    // to be rendered through react-markdown, which could not express underline
    // or colour and silently dropped both from every printed note.
    const renderedHtml = patientInfoHtml + content + resolvedPhotosHtml + signatureContent + disclaimerHtml;
    const printContent = `
          <!DOCTYPE html>
          <html lang="en">
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>${escapeHtml(documentTitle)} - ${escapeHtml(patientName)}</title>
              <style>
                /* Reset and base styles */
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }

                body {
                  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Segoe UI', system-ui, sans-serif;
                  line-height: 1.4;
                  color: #1f2937;
                  background: #ffffff;
                  font-size: 11pt;
                  max-width: 8.5in;
                  margin: 0 auto;
                  padding: 0.5in;
                  -webkit-font-smoothing: antialiased;
                  -moz-osx-font-smoothing: grayscale;
                  min-height: 100vh;
                  display: flex;
                  flex-direction: column;
                }

                main {
                  flex: 1;
                }

                /* Header and branding */
                .document-header {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-bottom: 1rem;
                  padding-bottom: 0.5rem;
                  border-bottom: 1px solid #111827;
                }

                .brand-section {
                  display: flex;
                  align-items: center;
                  gap: 12px;
                }

                                                 .brand-logo {
                  width: 50px;
                  height: 50px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }

                .brand-logo img {
                  width: 40px;
                  height: 40px;
                  object-fit: contain;
                }

                .brand-text {
                  display: flex;
                  flex-direction: column;
                }

                .brand-name {
                  font-size: 12pt;
                  font-weight: 600;
                  color: #111827;
                  letter-spacing: 0;
                }

                .brand-tagline {
                  font-size: 8pt;
                  color: #6b7280;
                  font-weight: 600;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }

                /* The document's own title, e.g. "History and Physical". */
                .document-title {
                  font-size: 17pt;
                  font-weight: 600;
                  color: #111827;
                  letter-spacing: 0;
                  margin: 0 0 0.75rem 0;
                  display: block;
                }

                /* Opening identifier block: label/value rows, no boxes. */
                .encounter-header {
                  margin-bottom: 1.25rem;
                  padding-bottom: 0.75rem;
                  border-bottom: 1px solid #d1d5db;
                }

                .identifiers .id-row {
                  display: flex;
                  align-items: baseline;
                  gap: 0.5rem;
                  padding: 1.5px 0;
                }

                .identifiers dt {
                  flex: 0 0 11rem;
                  color: #4b5563;
                  font-size: 10pt;
                }

                .identifiers dd {
                  margin: 0;
                  color: #111827;
                  font-size: 10pt;
                  font-weight: 600;
                }

                /* Letterhead: provider + clinic contact details */
                .letterhead {
                  display: flex;
                  flex-wrap: wrap;
                  justify-content: space-between;
                  gap: 12px;
                  margin-bottom: 1.25rem;
                  padding: 0 0 0.75rem 0;
                  background: none;
                  border: none;
                  border-bottom: 1px solid #d1d5db;
                  border-radius: 0;
                }

                .letterhead-block {
                  font-size: 9pt;
                  line-height: 1.4;
                  color: #4b5563;
                }

                .letterhead-name {
                  font-size: 9pt;
                  font-weight: 600;
                  color: #1f2937;
                  margin-bottom: 2px;
                }

                .letterhead-line {
                  color: #4b5563;
                }

                /* Typography hierarchy */
                h1 {
                  color: #111827;
                  font-size: 17pt;
                  font-weight: 600;
                  margin-bottom: 0.75rem;
                  letter-spacing: 0;
                }

                /* Note sections: the major divisions of the narrative. */
                h2 {
                  color: #111827;
                  font-size: 12pt;
                  font-weight: 600;
                  margin: 1.1rem 0 0.4rem 0;
                  padding-bottom: 2px;
                  border-bottom: 1px solid #d1d5db;
                  letter-spacing: 0;
                }

                /* Question labels: the subheading a paragraph of prose sits under. */
                h3 {
                  color: #111827;
                  font-size: 11pt;
                  font-weight: 600;
                  margin: 0.85rem 0 0.15rem 0;
                  letter-spacing: 0;
                }

                /* Prose under a subheading reads as body text, not a data field. */
                h3 + p {
                  margin-top: 0;
                }

                p {
                  margin: 0 0 0.5rem 0;
                  text-align: left;
                }

                /* Content sections */
                .medical-codes-info, .medical-info, .measurements-info, .medications-info {
                  margin-bottom: 1.25rem;
                }

                .medical-codes-title, .medical-title, .measurements-title, .medications-title, .note-photos-title {
                  font-size: 12pt;
                  font-weight: 600;
                  color: #111827;
                  margin: 0 0 0.4rem 0;
                  padding-bottom: 2px;
                  border-bottom: 1px solid #d1d5db;
                  letter-spacing: 0;
                }

                /* Flat columns rather than filled cards — this is a document. */
                .medical-codes-grid, .medical-info-grid, .measurements-grid {
                  display: grid;
                  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
                  gap: 10px 24px;
                  padding: 0;
                  background: none;
                  border-radius: 0;
                  border: none;
                }

                .medical-codes-grid, .medical-info-grid {
                  grid-template-columns: 1fr;
                  gap: 16px;
                }

                .medical-code-list {
                  display: flex;
                  flex-direction: column;
                  gap: 6px;
                  margin-top: 4px;
                }

                .medical-code-item {
                  font-size: 9pt;
                }

                .medical-code-main {
                  display: flex;
                  align-items: baseline;
                  gap: 8px;
                }

                .medical-code-value {
                  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                  font-weight: 700;
                  color: #1f2937;
                }

                .medical-code-description {
                  color: #4b5563;
                }

                .medical-code-modifiers {
                  display: flex;
                  align-items: baseline;
                  flex-wrap: wrap;
                  gap: 4px;
                  margin: 4px 0 0 42px;
                }

                /* One row per modifier so a long AMA label wraps under itself
                 * rather than stretching the chip row across the page. */
                .medical-code-modifier-list {
                  display: flex;
                  flex-direction: column;
                  gap: 3px;
                  min-width: 0;
                }

                .medical-code-modifier-row {
                  display: flex;
                  align-items: baseline;
                  gap: 6px;
                  min-width: 0;
                }

                .medical-code-modifier-description {
                  color: #4b5563;
                }

                .medical-code-modifiers-label {
                  color: #6b7280;
                  font-size: 8pt;
                  font-weight: 600;
                  margin-right: 2px;
                  text-transform: uppercase;
                  letter-spacing: 0.4px;
                }

                .medical-code-modifier {
                  display: inline-flex;
                  align-items: center;
                  padding: 1px 6px;
                  border-radius: 4px;
                  border: 1px solid #d1d5db;
                  background: #e5e7eb;
                  color: #374151;
                  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                  font-size: 9pt;
                  font-weight: 600;
                }

                .measurement-item {
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                }

                .measurement-label {
                  font-size: 8pt;
                  font-weight: 600;
                  color: #6b7280;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }

                .measurement-value {
                  font-size: 9pt;
                  font-weight: 600;
                  color: #1f2937;
                }

                .measurement-date {
                  font-size: 8pt;
                  font-weight: 400;
                  color: #9ca3af;
                  font-style: italic;
                  margin-top: 2px;
                }

                /* The gap between two medications has to out-measure the gap
                   between the rows inside one, or the list runs together. */
                .medication-item {
                  margin-bottom: 14px;
                  padding: 0 0 14px 0;
                  background: none;
                  border-radius: 0;
                  border: none;
                  border-bottom: 1px solid #d1d5db;
                }

                .medication-item:last-child {
                  margin-bottom: 0;
                  padding-bottom: 0;
                  border-bottom: none;
                }

                .medication-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  margin-bottom: 5px;
                }

                .medication-name {
                  font-size: 10pt;
                  font-weight: 600;
                  color: #1f2937;
                  line-height: 1.2;
                }

                .medication-dose {
                  font-size: 10pt;
                  font-weight: 600;
                  color: #111827;
                  background: none;
                  padding: 0;
                  border-radius: 0;
                  border: none;
                }

                .medication-detail {
                  margin-bottom: 2px;
                  font-size: 9pt;
                  line-height: 1.4;
                  color: #4b5563;
                }

                .detail-label {
                  font-weight: 600;
                  color: #374151;
                  margin-right: 4px;
                }

                .medication-date {
                  margin-top: 5px;
                  font-size: 8pt;
                  font-weight: 400;
                  color: #9ca3af;
                  font-style: italic;
                }

                .info-item {
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                }

                .info-item-wide {
                  grid-column: 1 / -1;
                }

                .info-label {
                  font-size: 9pt;
                  font-weight: 400;
                  color: #4b5563;
                  letter-spacing: 0;
                }

                .info-value {
                  font-size: 10pt;
                  font-weight: 600;
                  color: #111827;
                }

                /* Content styling */
                .content-section {
                  background: #ffffff;
                  border-radius: 8px;
                  padding: 24px;
                  margin: 1.5rem 0;
                  border: 1px solid rgb(var(--border));
                  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
                }

                p {
                  margin: 0.5rem 0;
                  line-height: 1.5;
                }

                /* Constrain inline content images (e.g. drawn signatures).
                   The header logo lives in <header>, so scoping to <main>
                   leaves it untouched. */
                main img {
                  max-width: 280px;
                  max-height: 120px;
                  height: auto;
                  border: 1px solid rgb(var(--border));
                  border-radius: 4px;
                }

                /* Clinical photos. The class selector outranks the signature
                   sizing above, which would otherwise shrink a photo to a
                   thumbnail. */
                .note-photos {
                  margin: 1.5rem 0;
                }

                .note-photos-grid {
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                  gap: 16px;
                }

                .note-photos figure {
                  margin: 0;
                  page-break-inside: avoid;
                  break-inside: avoid;
                }

                .note-photos img {
                  display: block;
                  width: 100%;
                  max-width: 100%;
                  max-height: 4.5in;
                  height: auto;
                  object-fit: contain;
                  border: 1px solid #e2e8f0;
                  border-radius: 4px;
                }

                .note-photos figcaption {
                  font-size: 8pt;
                  color: #6b7280;
                  margin-top: 4px;
                  line-height: 1.3;
                  overflow-wrap: anywhere;
                }

                .note-photo-missing {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  min-height: 1.5in;
                  padding: 12px;
                  font-size: 8pt;
                  font-style: italic;
                  color: #92400e;
                  text-align: center;
                  background: #fffbeb;
                  border: 1px dashed #fcd34d;
                  border-radius: 4px;
                }

                strong {
                  font-weight: 600;
                  color: #1f2937;
                }

                em {
                  font-style: italic;
                  color: #4b5563;
                }

                /* Lists */
                ul, ol {
                  margin: 0.5rem 0;
                  padding-left: 1.5rem;
                }

                li {
                  margin: 0.25rem 0;
                  line-height: 1.5;
                }

                /* Note sections are h2, so a section opening with a bulleted
                   list must read as a list — not a filled grid of tiles. */
                ul, ol {
                  margin: 0.25rem 0 0.6rem 0;
                  padding-left: 1.25rem;
                }

                li {
                  margin: 0 0 0.15rem 0;
                  font-size: 11pt;
                  line-height: 1.45;
                }

                li strong {
                  color: #111827;
                }

                /* Code and preformatted text */
                code {
                  background: #f1f5f9;
                  color: #1e293b;
                  padding: 2px 6px;
                  border-radius: 4px;
                  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                  font-size: 9pt;
                  border: 1px solid #e2e8f0;
                }

                pre {
                  background: #f8fafc;
                  padding: 20px;
                  border-radius: 8px;
                  overflow-x: auto;
                  margin: 1.5rem 0;
                  border: 1px solid #e5e7eb;
                }

                pre code {
                  background: transparent;
                  padding: 0;
                  border: none;
                  font-size: 9pt;
                  line-height: 1.6;
                }

                /* Dividers */
                hr {
                  border: none;
                  border-top: 1px solid #d1d5db;
                  height: 0;
                  margin: 1.25rem 0;
                }

                /* Footer */
                .document-footer {
                  margin-top: 3rem;
                  padding-top: 1.5rem;
                  border-top: 1px solid rgb(var(--border));
                  font-size: 8pt;
                  color: #6b7280;
                  text-align: center;
                  font-style: italic;
                }

                .footer-disclaimer {
                  background: #fffbeb;
                  border: 1px solid #f59e0b;
                  border-radius: 6px;
                  padding: 12px;
                  margin-top: 1rem;
                  color: #92400e;
                  text-align: left;
                }


                /* Print-specific styles */
                @media print {
                  body {
                    margin: 0;
                    padding: 0.25in;
                    font-size: 10pt;
                  }

                  .document-header {
                    margin-bottom: 1rem;
                  }

                  .patient-demographics, .visit-info, .medical-codes-info, .medical-info, .measurements-info {
                    page-break-inside: avoid;
                    margin-bottom: 1rem;
                  }

                  .content-section {
                    page-break-inside: avoid;
                    box-shadow: none;
                    border: 1px solid #d1d5db;
                  }

                  .demographics-title, .visit-title, .medical-title, .measurements-title, .medications-title,
                  .note-photos-title,
                  h1, h2, h3 {
                    page-break-after: avoid;
                  }

                  .note-photos figure {
                    page-break-inside: avoid;
                    break-inside: avoid;
                  }

                  .medical-codes-grid, .medical-info-grid, .measurements-grid {
                    box-shadow: none;
                  }

                  .medication-item {
                    page-break-inside: avoid;
                    box-shadow: none;
                  }

                  .no-print {
                    display: none;
                  }
                }

                @page {
                  margin: 0.25in 0.25in 0.75in 0.25in;
                  size: letter;
                  @top-left { content: ""; }
                  @top-center { content: ""; }
                  @top-right { content: ""; }
                  @bottom-left { content: ""; }
                  @bottom-center {
                    content: counter(page) " of " counter(pages);
                    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Segoe UI', system-ui, sans-serif;
                    font-size: 8pt;
                    color: #6b7280;
                  }
                  @bottom-right { content: ""; }
                }
              </style>
            </head>
            <body>
              <header class="document-header">
                <div class="brand-section">
                  <div class="brand-logo">
                    <img src="${logoDataUrl || tenant?.logoUrl || "/logos/decoda-favicon.ico"}" alt="${tenant?.name || "Decoda Health"}" />
                  </div>
                  <div class="brand-text">
                    <div class="brand-name">${tenant?.name || "Decoda Health"}</div>
                  </div>
                </div>
              </header>

              ${letterheadHtml}

              <main>
                ${renderedHtml}
              </main>

              <footer class="document-footer">
                  <div class="footer-disclaimer">
                    <strong>Medical Note:</strong> This document contains confidential medical information.
                    Handle in accordance with HIPAA and applicable healthcare privacy regulations.
                  </div>
              </footer>
            </body>
          </html>
        `;
    return {
      success: true,
      html: printContent
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unexpected error occurred"
    };
  }
};
export const printMarkdown = async (options: PrintMarkdownOptions): Promise<{
  success: boolean;
  error?: string;
}> => {
  // Opened before anything is awaited: `window.open` only succeeds while the
  // click's transient user activation is live, and building the document
  // fetches the patient, the photos, and the modifier labels.
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    return {
      success: false,
      error: "Unable to open print window. Please check your browser settings."
    };
  }
  const built = await buildNotePrintHtml(options);
  if (!built.success) {
    // The blank popup is useless without a document, and leaving it up reads as
    // a hung print to the provider.
    printWindow.close();
    return {
      success: false,
      error: built.error
    };
  }
  writeToPrintWindow(printWindow, built.html);
  return {
    success: true
  };
};

// ============================================================================
// Care Plan Printing & Email
// ============================================================================

export interface CarePlanHtmlStep {
  stepNumber: number;
  scheduledDate: string;
  status: string;
  serviceIds: string[];
}
interface CarePlanHtmlOptions {
  carePlanName: string;
  patientName: string;
  steps: CarePlanHtmlStep[];
  serviceById: Map<string, ServiceDetail>;
  tenant: PrivateTenant | null;
}

/**
 * Generates email-safe HTML for a care plan using inline styles and table layout.
 * Provider canvas printing is separate from this email summary.
 */
export function generateCarePlanHtml({
  carePlanName,
  patientName,
  steps,
  serviceById,
  tenant
}: CarePlanHtmlOptions): string {
  const baseColor = tenant?.brandPrimaryColor || "#4a5d4e";
  const logoUrl = tenant?.receiptLogoUrl || tenant?.logoUrl || "";
  const tenantName = tenant?.name || "Decoda Health";
  const generatedDate = formatDate(new Date(), true, true);
  const font = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
  const statusStyles: Record<string, string> = {
    COMPLETED: "display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;background:#dcfce7;color:#166534;",
    MISSED: "display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;background:#fef2f2;color:#991b1b;",
    SKIPPED: "display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;background:#fef2f2;color:#991b1b;",
    DEFAULT: "display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;background:#f1f5f9;color:#475569;"
  };
  const stepRows = steps.map(step => {
    const services = step.serviceIds.map(id => serviceById.get(id)?.name).filter(Boolean);
    const serviceText = services.length > 0 ? services.join(", ") : "No services assigned";
    const dateText = step.scheduledDate ? formatDate(step.scheduledDate, true, true) : "Not scheduled";
    const statusLabel = step.status.charAt(0) + step.status.slice(1).toLowerCase();
    const statusStyle = statusStyles[step.status] || statusStyles["DEFAULT"];
    return `<tr>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:11px;color:#6b7280;font-weight:600;width:40px;">${step.stepNumber}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:11px;color:#1f2937;font-weight:500;">${serviceText}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:11px;color:#374151;white-space:nowrap;">${dateText}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:11px;"><span style="${statusStyle}">${statusLabel}</span></td>
      </tr>`;
  }).join("");
  const logoHtml = logoUrl ? `<td style="width:40px;padding-right:12px;vertical-align:middle;"><img src="${logoUrl}" alt="${tenantName}" width="40" height="40" style="width:40px;height:40px;object-fit:contain;" /></td>` : "";
  return `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:612px;margin:0 auto;font-family:${font};color:#1f2937;background:#ffffff;">
  <!-- Header -->
  <tr>
    <td style="padding:16px 0 12px 0;border-bottom:3px solid ${baseColor};">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          ${logoHtml}
          <td style="vertical-align:middle;">
            <span style="font-size:18px;font-weight:700;color:${baseColor};">${tenantName}</span>
          </td>
          <td style="text-align:right;vertical-align:middle;">
            <span style="background:${baseColor}15;color:${baseColor};padding:8px 16px;border-radius:20px;font-size:11px;font-weight:600;border:1px solid ${baseColor}40;text-transform:uppercase;letter-spacing:0.5px;">Care Plan</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>

  <!-- Info bar -->
  <tr>
    <td style="padding:16px 0;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;">
        <tr>
          <td style="padding:16px;width:33%;">
            <div style="font-size:9px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Patient</div>
            <div style="font-size:13px;font-weight:600;color:#1f2937;margin-top:2px;">${patientName}</div>
          </td>
          <td style="padding:16px;width:34%;">
            <div style="font-size:9px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Care Plan</div>
            <div style="font-size:13px;font-weight:600;color:#1f2937;margin-top:2px;">${carePlanName}</div>
          </td>
          <td style="padding:16px;width:33%;">
            <div style="font-size:9px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;">Date</div>
            <div style="font-size:13px;font-weight:600;color:#1f2937;margin-top:2px;">${generatedDate}</div>
          </td>
        </tr>
      </table>
    </td>
  </tr>

  <!-- Steps table -->
  <tr>
    <td>
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
        <tr>
          <th style="text-align:left;font-size:10px;font-weight:600;color:${baseColor};padding:8px 12px;border-bottom:2px solid ${baseColor}30;text-transform:uppercase;letter-spacing:0.5px;">#</th>
          <th style="text-align:left;font-size:10px;font-weight:600;color:${baseColor};padding:8px 12px;border-bottom:2px solid ${baseColor}30;text-transform:uppercase;letter-spacing:0.5px;">Services</th>
          <th style="text-align:left;font-size:10px;font-weight:600;color:${baseColor};padding:8px 12px;border-bottom:2px solid ${baseColor}30;text-transform:uppercase;letter-spacing:0.5px;">Date</th>
          <th style="text-align:left;font-size:10px;font-weight:600;color:${baseColor};padding:8px 12px;border-bottom:2px solid ${baseColor}30;text-transform:uppercase;letter-spacing:0.5px;">Status</th>
        </tr>
        ${stepRows}
      </table>
    </td>
  </tr>

  <!-- Footer -->
  <tr>
    <td style="padding-top:24px;">
      <div style="padding:12px;background:#fffbeb;border:1px solid #f59e0b;border-radius:6px;font-size:9px;color:#92400e;">
        <strong>Care Plan:</strong> This document contains confidential medical information.
        Handle in accordance with HIPAA and applicable healthcare privacy regulations.
      </div>
    </td>
  </tr>
</table>`;
}
export interface NoteDocumentOptions {
  hideMedications?: boolean;
  hideMedicationDosage?: boolean;
  hideMedicalInformation?: boolean;
  photosHtml?: string | (() => Promise<string>);
  signatureContent?: string;
  encounterType?: string;
}
export const usePrintNote = () => {
  const $ = _c(12);
  const {
    tenant
  } = useTenant();
  const {
    toast
  } = useToast();
  let t0;
  if ($[0] !== tenant?.subDomain) {
    t0 = async (noteItem, content, options) => {
      const hasPrintableContent = !!content.trim() || !!options.photosHtml || !!options.signatureContent;
      if (!noteItem.patient?.firstName || !noteItem.patient?.lastName || !hasPrintableContent) {
        return {
          success: false,
          error: "Missing required note data"
        };
      }
      const modifierDescriptionsPromise = queryClient.fetchQuery(medicalCodeQueryOptions.modifierDescriptions(noteModifierCodes(noteItem))).then(_temp).catch(_temp2);
      const modifierDescriptions = () => modifierDescriptionsPromise;
      if (tenant?.subDomain === "wellness-by-approxie") {
        return {
          success: true,
          noteItem,
          modifierDescriptions
        };
      }
      const providerIds = noteItem.providers.map(_temp3);
      const roles = await queryClient.fetchQuery(providerQueryOptions.roles(providerIds));
      if (!roles) {
        return {
          success: false,
          error: "Unable to fetch provider roles"
        };
      }
      const providersWithRequiredRoles = noteItem.providers?.filter(p_0 => roles[p_0.id]?.some(_temp4));
      if (providersWithRequiredRoles.length === 0) {
        return {
          success: false,
          error: "You must have a provider to sign off on this note to print it. Please find a provider to make an edit."
        };
      }
      return {
        success: true,
        noteItem: {
          ...noteItem,
          providers: providersWithRequiredRoles
        },
        modifierDescriptions
      };
    };
    $[0] = tenant?.subDomain;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const prepareNoteDocument = t0;
  let t1;
  if ($[2] !== prepareNoteDocument || $[3] !== tenant || $[4] !== toast) {
    t1 = async (noteItem_0, content_0, t2) => {
      const options_0 = t2 === undefined ? {} : t2;
      const prepared = await prepareNoteDocument(noteItem_0, content_0, options_0);
      if (!prepared.success) {
        toast({
          title: "Error",
          description: prepared.error,
          variant: "error"
        });
        return {
          success: false,
          error: prepared.error
        };
      }
      const result = await printMarkdown({
        content: content_0,
        noteItem: prepared.noteItem,
        tenant,
        hideMedications: options_0.hideMedications,
        hideMedicationDosage: options_0.hideMedicationDosage,
        hideMedicalInformation: options_0.hideMedicalInformation,
        photosHtml: options_0.photosHtml,
        signatureContent: options_0.signatureContent,
        encounterType: options_0.encounterType,
        modifierDescriptions: prepared.modifierDescriptions
      });
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error || "Unable to prepare document for printing",
          variant: "error"
        });
      }
      return result;
    };
    $[2] = prepareNoteDocument;
    $[3] = tenant;
    $[4] = toast;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const printNote = t1;
  let t2;
  if ($[6] !== prepareNoteDocument || $[7] !== tenant) {
    t2 = async (noteItem_1, content_1, t3) => {
      const options_1 = t3 === undefined ? {} : t3;
      const prepared_0 = await prepareNoteDocument(noteItem_1, content_1, options_1);
      if (!prepared_0.success) {
        return {
          success: false,
          error: prepared_0.error
        };
      }
      return await buildNotePrintHtml({
        content: content_1,
        noteItem: prepared_0.noteItem,
        tenant,
        hideMedications: options_1.hideMedications,
        hideMedicationDosage: options_1.hideMedicationDosage,
        hideMedicalInformation: options_1.hideMedicalInformation,
        photosHtml: options_1.photosHtml,
        signatureContent: options_1.signatureContent,
        encounterType: options_1.encounterType,
        modifierDescriptions: prepared_0.modifierDescriptions,
        logoDataUrl: options_1.logoDataUrl
      });
    };
    $[6] = prepareNoteDocument;
    $[7] = tenant;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const buildNoteDocumentHtml = t2;
  let t3;
  if ($[9] !== buildNoteDocumentHtml || $[10] !== printNote) {
    t3 = {
      printNote,
      buildNoteDocumentHtml
    };
    $[9] = buildNoteDocumentHtml;
    $[10] = printNote;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
function _temp(modifiers) {
  return buildModifierDescriptionMap(modifiers ?? []);
}
function _temp2() {
  return {} as ModifierDescriptionMap;
}
function _temp3(p) {
  return p.id;
}
function _temp4(role) {
  return role.userType === RoleType.PROVIDER || role.userType === RoleType.ADMIN;
}